import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { GraduationCap, Plus, Search, Pencil, Trash2, Settings2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/shared/services/client'
import { useAuth } from '@/auth/context/AuthContext'
import {
  useConfiguracionesCEMC,
  useToggleActivoCEMC,
  useDeleteConfiguracionCEMC,
} from '../hooks/useCEMC'
import { ConfiguracionFormSheet } from '../components/ConfiguracionFormSheet'
import type { ConfiguracionCEMC } from '../types'

export function ConfiguracionCEMCPage() {
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()
  const isTonalAdmin = user?.is_superuser || user?.groups?.includes('admin')
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ConfiguracionCEMC | null>(null)

  useEffect(() => {
    if (!authLoading && !isTonalAdmin) navigate('/cemc-educacion', { replace: true })
  }, [authLoading, isTonalAdmin, navigate])

  const { data, isLoading } = useConfiguracionesCEMC({ page: 1, search: search || undefined })
  const { data: gruposRaw = [] } = useQuery<{ grupo_id: number; grupo_nombre: string }[]>({
    queryKey: ['grupos'],
    queryFn: async () => {
      const res = await apiClient.get('/group-extensions/all/')
      return res.data
    },
  })
  const grupos = gruposRaw.map((g) => ({ id: g.grupo_id, name: g.grupo_nombre }))

  const toggleMutation = useToggleActivoCEMC()
  const deleteMutation = useDeleteConfiguracionCEMC()

  const handleDelete = async (config: ConfiguracionCEMC) => {
    if (window.confirm(`¿Eliminar la configuración "${config.nombre}"?`)) {
      await deleteMutation.mutateAsync(config.id)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <GraduationCap className="h-7 w-7 text-violet-600" />
          Configuración CEMC
        </h1>
        <Button
          onClick={() => { setEditing(null); setFormOpen(true) }}
          className="bg-violet-700 hover:bg-violet-800 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nueva configuración
        </Button>
      </div>

      <div className="flex items-center gap-2 max-w-sm">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Configuraciones</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Grupo</TableHead>
                <TableHead className="text-center">Fuentes</TableHead>
                <TableHead className="text-center">Cursos</TableHead>
                <TableHead className="text-center">Activo</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? [1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    {[1,2,3,4,5,6].map((j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}
                  </TableRow>
                ))
                : data?.results?.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell className="font-medium">{config.nombre}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{config.grupo_nombre}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{config.fuentes?.length || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{config.cursos?.length || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={config.activo}
                        onCheckedChange={() => toggleMutation.mutate(config.id)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => navigate(`/cemc-educacion/configuracion/${config.id}/fuentes`)}
                          title="Gestionar fuentes y cursos"
                        >
                          <Settings2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => { setEditing(config); setFormOpen(true) }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(config)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ConfiguracionFormSheet
        open={formOpen}
        onClose={() => setFormOpen(false)}
        grupos={grupos}
        editing={editing}
      />
    </div>
  )
}
